import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Users, Globe2, Medal, ChevronLeft, ChevronRight } from "lucide-react";
import { AGE_CATEGORY_COUNT } from "@/lib/brand";

const CHAPTERS = [
  { icon: Trophy, eyebrow: "Chapter 01", title: "Centre for Sports Excellence", body: "Bengaluru's flagship aquatic facility, home of Dolphin Aquatics. A 50m, 10-lane Olympic-size competition pool with 25m warm-up pools, electronic timing, and 1,500 spectator seats.", color: "#00F0FF", img: "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg" },
  { icon: Users, eyebrow: "Chapter 02", title: "All generations, one start line", body: `From 25-year-old club swimmers to 80+ legends still racing. ${AGE_CATEGORY_COUNT} 5-year age bands ensure every athlete meets peers in their own generation.`, color: "#7DD3FC", img: "/images/chapter2.png" },
  { icon: Globe2, eyebrow: "Chapter 03", title: "Asia, united in lanes", body: "Hosted by Swimming Federation of India under Asia Aquatics — the largest Masters aquatics gathering the continent has ever staged.", color: "#34D399", img: "https://images.pexels.com/photos/261185/pexels-photo-261185.jpeg" },
  { icon: Medal, eyebrow: "Chapter 04", title: "Seven days. Fifteen events.", body: "Competition begins on October 4th - 10th, Time Trials begin at 10:00 AM IST.", color: "#FBBF24", img: "https://images.pexels.com/photos/1263348/pexels-photo-1263348.jpeg" },
];

export default function PinnedStory() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const go = (i) => {
    setDirection(i > activeIndex ? 1 : -1);
    setActiveIndex(i);
  };
  const prev = () => activeIndex > 0 && go(activeIndex - 1);
  const next = () => activeIndex < CHAPTERS.length - 1 && go(activeIndex + 1);

  const c = CHAPTERS[activeIndex];

  return (
    <section data-testid="pinned-story" className="relative bg-[#0B1429] py-16 md:py-24 px-4 sm:px-6 lg:px-16 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Chapter counter + arrows */}
        <div className="flex items-center justify-between mb-10">
          <div className="text-xs font-mono tracking-[0.25em] text-[#94A3B8] uppercase">
            {activeIndex + 1} / {CHAPTERS.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={activeIndex === 0}
              aria-label="Previous chapter"
              className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-[#00F0FF]/50 disabled:opacity-20 disabled:cursor-not-allowed transition-all touch-manipulation"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              disabled={activeIndex === CHAPTERS.length - 1}
              aria-label="Next chapter"
              className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-white hover:border-[#00F0FF]/50 disabled:opacity-20 disabled:cursor-not-allowed transition-all touch-manipulation"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Animated chapter content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeIndex}
            custom={direction}
            variants={{
              enter: (d) => ({ x: d * 60, opacity: 0 }),
              center: { x: 0, opacity: 1 },
              exit: (d) => ({ x: d * -60, opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div className="relative h-[250px] sm:h-[350px] lg:h-[52vh] rounded-2xl overflow-hidden border border-white/10">
              <img src={c.img} alt="" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 mix-blend-multiply" style={{ background: c.color, opacity: 0.4 }} />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full flex items-center justify-center border" style={{ borderColor: c.color, background: c.color + "18" }}>
                  <c.icon className="w-5 h-5" style={{ color: c.color }} strokeWidth={1.5} />
                </div>
                <div className="text-xs uppercase tracking-[0.3em]" style={{ color: c.color }}>{c.eyebrow}</div>
              </div>
              <h3 className="font-display font-black text-2xl sm:text-4xl md:text-5xl leading-[0.95] mb-4 sm:mb-5 text-white">{c.title}</h3>
              <p className="text-[#CBD5E1] text-base sm:text-lg leading-relaxed max-w-lg">{c.body}</p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav dots — 44x44 tap target, small visual dot centered inside */}
        <div className="flex items-center mt-4">
          {CHAPTERS.map((_, i) => (
            <button
              key={i}
              onClick={() => go(i)}
              aria-label={`Go to chapter ${i + 1}: ${CHAPTERS[i].title}`}
              aria-pressed={i === activeIndex}
              className="w-11 h-11 flex items-center justify-center touch-manipulation"
            >
              <span
                className={`block transition-all duration-300 rounded-full ${i === activeIndex
                  ? "w-6 h-2 bg-[#00F0FF]"
                  : "w-2 h-2 bg-white/20"
                  }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
